import FormattedEther from '@/presentation/components/elements/FormattedEther';
import { useEffect, useRef } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslations } from 'use-intl';
import type { SetRangeFormFields } from '../types';
import { parseEther } from 'viem';
import { Collection } from 'nftx.js';
import useCustomFormatter from '@/presentation/utils/numberFormatter';

type Form = UseFormReturn<SetRangeFormFields>;

interface Props {
  symbol: string;
  currentTick: string;
  control: Form['control'];
  setValue: Form['setValue'];
  isNewVault: boolean;
  isNewPool: boolean;
  collection: Collection | undefined;
  setDisabled: (v: boolean) => void;
  onPriceChange: (v: string) => void;
  showTicks: boolean;
}

const Price = ({
  symbol,
  currentTick,
  control,
  setValue,
  isNewVault,
  isNewPool,
  setDisabled,
  onPriceChange,
  collection,
  showTicks,
}: Props) => {
  // formatting
  const t = useTranslations('CreateLiquidityPositionForms.SetRange');

  // Used for placeholder
  const { bigintToEth } = useCustomFormatter();
  const floorPrice = collection?.floorPrice
    ? `${bigintToEth(collection.floorPrice)}`
    : '';

  // watch for when the actual form tick values are updated - source of truth
  const { price = '0', tick = currentTick } = useWatch({
    control,
  });

  const priceRef = useRef<HTMLInputElement>(null);

  const hasPrice = !!price && !!Number(price) && !Number.isNaN(Number(price));

  useEffect(() => {
    // Focus the price input when we land on this step and it's empty
    if (!hasPrice) {
      priceRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <aside className="bg-gray-200 p-3 rounded-lg font-medium text-center">
      {isNewVault || isNewPool ? (
        <Controller
          control={control}
          name="price"
          rules={{
            validate: (value) => {
              if (!value || !Number(value)) {
                return 'Enter an initial spot price';
              }
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <div className="flex items-center justify-center">
                <label className="mr-2">{t('setPrice')}</label>
                <div>
                  <input
                    {...field}
                    type="number"
                    step={0.1}
                    className="bg-gray-50 text-right border border-gray-300 rounded-md mr-2 focus:border-gray-400 focus:outline-0 focus:ring-0 text-lg py-1 px-2 w-[10ch]"
                    onChange={(e) => {
                      field.onChange(e);
                      setDisabled(true);
                      onPriceChange(e.target.value);
                    }}
                    placeholder={floorPrice}
                    ref={priceRef}
                  />
                </div>
                <span className="text-gray-500">{'ETH'}</span>
                {floorPrice && floorPrice !== '0' && (
                  <button
                    type="button"
                    className="ml-4 text-sm underline text-gray-500 hover:no-underline"
                    onClick={() => {
                      setValue('price', floorPrice);
                      setDisabled(true);
                      onPriceChange(floorPrice);
                    }}
                  >
                    {t('setFloor')} {floorPrice} ETH
                  </button>
                )}
              </div>
              {!!error && <div className="text-red-500">{error.message}</div>}
            </div>
          )}
        />
      ) : currentTick ? (
        t.rich('currentPrice', {
          price: (<FormattedEther value={parseEther(price)} />) as any,
          symbol,
        })
      ) : null}
      {showTicks && (
        <p className="text-sm text-gray-500">{`current tick: ${tick}`}</p>
      )}
    </aside>
  );
};

export default Price;
